import { useMemo } from 'react'
import { WorkspaceFiles } from '../store/reducers/workspaceReducer'

export interface TreeNode {
  title: string
  value: string
  disabled?: boolean
  children?: TreeNode[]
}

interface TreeBuilderNode {
  children: Record<string, TreeBuilderNode>
  file: WorkspaceFiles | null
}

const convertToTree = (
  fileRoots: Record<string, TreeBuilderNode>,
  disabledIds: string[]
): TreeNode[] => {
  return Object.entries(fileRoots).map(([name, node]) => {
    const isFile = node.file !== null
    const value = isFile ? node.file!.id : `folder:${name}`
    return {
      title: name,
      value,
      disabled: isFile ? disabledIds.includes(value) : true,
      children: convertToTree(node.children, disabledIds), // recurse over children
    }
  })
}

const buildTreeData = (
  files: WorkspaceFiles[],
  disabledIds: string[]
): TreeNode[] => {
  if (files.length === 0) return []
  const fileRoots = files.reduce<Record<string, TreeBuilderNode>>((idx, file) => {
    const fileParts = file.path.split('/').slice(1) // skip the root
    let currentNode = idx

    fileParts.forEach((part, index) => {
    
      if (part === '') return
      if (!currentNode[part]) {
        currentNode[part] = { children: {}, file: null }
      }

      if (index === fileParts.length - 1) {
        currentNode[part].file = file
      }

      currentNode = currentNode[part].children
    })

    return idx
  }, {})

  return convertToTree(fileRoots, disabledIds)
}

interface UseFilepathTreeProps {
  files: WorkspaceFiles[]
  disabledIds: string[]
}

export const useFilepathTree = ({
  files,
  disabledIds,
}: UseFilepathTreeProps) => {
  const treeData = useMemo(
    () => buildTreeData(files, disabledIds),
    [files, disabledIds]
  )

  return { treeData }
}

export default useFilepathTree
